import { useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';

/**
 * On every step: name the browser tab after the step (or `documentTitle`,
 * when the heading is too personal for the tab) and move focus to the
 * step's heading, so screen-reader and keyboard users land on the new content
 * rather than where the previous step left them (ADR-025). A language switch
 * rewrites the title in place without changing the step, so the first title
 * change after one renames the tab but leaves focus on the switch the member
 * just used. That title can arrive a render later than the language, when it
 * comes from content refetched in the new language.
 */
export function useStepHeading(title: string, documentTitle: string = title) {
  const { t, i18n } = useTranslation('signup');
  const suffix = t('documentTitleSuffix');
  const language = i18n.language;
  const headingRef = useRef<HTMLHeadingElement>(null);
  const shown = useRef<{ title: string; language: string } | null>(null);
  const translating = useRef(false);
  useEffect(() => {
    document.title = `${documentTitle} | ${suffix}`;
    const previous = shown.current;
    shown.current = { title, language };
    if (previous && previous.language !== language) translating.current = true;
    if (previous && previous.title === title) return;
    if (translating.current) {
      translating.current = false;
      return;
    }
    headingRef.current?.focus();
  }, [title, documentTitle, suffix, language]);
  return headingRef;
}
